'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import VideoLightbox from '../fragments/video-lightbox/VideoLightbox';

const VideoLightboxContext = createContext({
  open: () => {},
  close: () => {},
  isOpen: false,
});

/**
 * Provides a single, app-wide video lightbox. Mount once near the root (see
 * _app.js) so every block shares one player instance.
 *
 *   const { open } = useVideoLightbox();
 *   open(video, posterUrl); // video = { url, width, height }
 */
export function VideoLightboxProvider({ children }) {
  const [state, setState] = useState({
    isOpen: false,
    src: '',
    poster: '',
    ratio: 16 / 9,
  });

  const open = useCallback((video, poster = '') => {
    const src = video?.url || '';
    if (!src) return;
    const ratio =
      video?.width && video?.height ? video.width / video.height : 16 / 9;
    setState({ isOpen: true, src, poster, ratio });
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const value = useMemo(
    () => ({ open, close, isOpen: state.isOpen }),
    [open, close, state.isOpen]
  );

  return (
    <VideoLightboxContext.Provider value={value}>
      {children}
      <VideoLightbox
        isOpen={state.isOpen}
        src={state.src}
        poster={state.poster}
        ratio={state.ratio}
        onClose={close}
      />
    </VideoLightboxContext.Provider>
  );
}

export const useVideoLightbox = () => useContext(VideoLightboxContext);
